import type { ReactElement } from 'react';

import { SURVEY_OTHER, type SurveyChoiceQuestion } from '@/shared/config';

import { QuestionTitle } from './question-title';

type Props = {
  question: SurveyChoiceQuestion;
  /** 하나 고르는 문항은 문자열, 여러 개 고르는 문항은 목록. */
  value: string | readonly string[];
  /** 「기타」 내용. 기타가 없는 문항(4번)은 undefined. */
  other: string | undefined;
  error: string | undefined;
  onChange: (next: string | readonly string[]) => void;
  onOtherChange: (text: string) => void;
};

const optionClass =
  'flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-card px-3.5 py-3 ' +
  'text-[13.5px] leading-[1.55] transition-colors hover:border-foreground/40 ' +
  'has-checked:border-foreground has-checked:bg-secondary ' +
  'has-disabled:cursor-not-allowed has-disabled:opacity-45 has-disabled:hover:border-border ' +
  'has-focus-visible:ring-[3px] has-focus-visible:ring-primary';

const toggle = (list: readonly string[], option: string): readonly string[] =>
  list.includes(option) ? list.filter((item) => item !== option) : [...list, option];

/**
 * 고르는 문항 한 개 (1~6번).
 *
 * 「최대 3개」는 고르는 순간 막는다 — 제출할 때 알려 주면 무엇을 뺄지 다시 고민하게 만든다.
 * 넷째를 누를 수 없게 나머지 보기를 잠그고, 몇 개 골랐는지 글자로도 알린다.
 */
export function ChoiceQuestion({
  question,
  value,
  other,
  error,
  onChange,
  onOtherChange,
}: Props): ReactElement {
  const { key, kind, maxPicks, options } = question;
  const picks = typeof value === 'string' ? (value ? [value] : []) : value;
  const isFull = maxPicks !== undefined && picks.length >= maxPicks;
  const isOtherOpen = other !== undefined && picks.includes(SURVEY_OTHER);
  const errorId = `survey-${key}-error`;

  return (
    <fieldset
      id={`survey-q-${key}`}
      aria-describedby={error ? errorId : undefined}
      className="flex scroll-mt-36 flex-col gap-4"
    >
      <QuestionTitle
        as="legend"
        number={question.number}
        title={question.title}
        required={question.required}
      />
      {/* 안내 문구는 제목에 붙여 읽히게 제목 여백을 절반 거둔다 — 보기와의 간격은 gap 이 준다 */}
      {question.hint ? (
        <p className="-mt-2 text-[12.5px] text-muted-foreground">{question.hint}</p>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const isChecked = picks.includes(option);
          return (
            <label key={option} className={optionClass}>
              <input
                type={kind === 'single' ? 'radio' : 'checkbox'}
                name={`survey-${key}`}
                value={option}
                checked={isChecked}
                disabled={isFull && !isChecked}
                aria-invalid={error ? true : undefined}
                onChange={() => onChange(kind === 'single' ? option : toggle(picks, option))}
                className="mt-[3px] size-4 shrink-0 accent-primary"
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>

      {maxPicks !== undefined ? (
        <p aria-live="polite" className="text-[12px] text-muted-foreground">
          최대 {maxPicks}개 · <span data-numeric="">{picks.length}</span>개 선택
        </p>
      ) : null}

      {/* 「기타」로 끝내면 아무 정보도 안 남는다 — 실제로 쓸 칸을 연다 */}
      {isOtherOpen ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`survey-${key}-other`} className="text-[12.5px] font-medium">
            「기타」 내용
          </label>
          <input
            id={`survey-${key}-other`}
            type="text"
            maxLength={100}
            value={other}
            placeholder="어떤 것인지 적어 주세요"
            onChange={(event) => onOtherChange(event.target.value)}
            className="h-10 rounded-md border border-border bg-card px-3 text-sm focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary"
          />
        </div>
      ) : null}

      {error ? (
        <p id={errorId} className="text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
